// React
import { ReactElement, useEffect, useState } from 'react';
// Next
import { GetServerSideProps, NextPage } from 'next';
import { i18n } from 'next-i18next.config';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
// Features
import { ACCESS_TOKEN_COOKIE_NAME } from '@utmb/features/sso/keycloak';
//import { DiscoverBanner } from '@utmb/features/single-runner/discover-banner/DiscoverBanner';
import { Layout } from '@utmb/layouts/layout-base';
// import { ResultsTable } from '@utmb/features/single-runner/results-table/ResultsTable';
// import { RunnerHeader } from '@utmb/features/single-runner/runner-header/RunnerHeader';
// import { RunnerStatistics } from '@utmb/features/single-runner/runner-statistics/RunnerStatistics';
// import PerformanceIndex from '@utmb/features/single-runner/performance-index/PerformanceIndex';
// import RunnerMoreDetails from '@utmb/features/single-runner/runner-more-details/RunnerMoreDetails';
// Contexts
import { PageProviders } from '@utmb/contexts/index';
import { useGlobal } from '@utmb/contexts/GlobalContext';
import { useUser } from '@utmb/contexts/AuthContext';
import { useTenancy } from '@utmb/contexts/TenancyContext';
// Types
//import { BackgroundColorEnum } from '@utmb/types/enum/BackgroundColorEnum';
import { PageView, PageDefaultProps, LangEnum, GlobalView } from '@utmb/types/index';
import RaceYearResultForRunnerPaginatedView from '@utmb/types/view/RaceYearResultForRunnerPaginatedView';
import RunnerStatisticView from '@utmb/types/view/RunnerStatisticView';
import RunnerView from '@utmb/types/view/RunnerView';
import RunnerIndexView from '@utmb/types/view/RunnerIndexView';
import FrozenIndexView from '@utmb/types/view/FrozenIndexView';
import { SexEnum } from '@utmb/types/enum/SexEnum';
import { PiCategoryEnum } from '@utmb/types/enum/PiCategoryEnum';
// Utils
import { fetchTheAPI } from '@utmb/utils/utmb';
import { getGender } from '@utmb/utils/gender';
import isMobile from '@utmb/utils/is-mobile';
import { pIdToCloudinary } from '@utmb/utils/cloudinary';
import { RunnerHeaderRemake } from '@utmb/design-system/index/runner-header-remake/runner-header-remake';
import { IndexRow } from '@utmb/design-system/index/index-row/index-row';
import { IndexStatisticsPalmaresArea } from '@utmb/design-system/index/index-statistics-palmares-area/index-statistics-palmares-area';
import styles from './runner.module.scss';
interface PageProps extends PageDefaultProps, PageView, RunnerView {}

const Runner: NextPage<PageProps> = (props: PageProps): ReactElement => {
    const {
        profilePicture,
        fullname,
        gender,
        performanceIndexes: performanceIndexesProp,
        frozenIndexes: frozenIndexesProp,
        results: resultsProp,
        host,
        isLogged,
        isMobile,
        runnerStatistic: runnerStatisticProp,
        club,
        // sponsor,
        // description,
        team,
        // socials,
        // website,
    } = props;

    const { t } = useTranslation();
    const { locale, query } = useRouter();
    const { global } = useGlobal();
    const { tenantId } = useTenancy();
    const { isAuthenticated, getToken, me } = useUser();

    const { event, eventsTopBar, mobileBottomBar, navigation, simpleMenu, footer, partners, languages }: GlobalView = global;

    const [results, setResults] = useState<RaceYearResultForRunnerPaginatedView>(resultsProp);
    const [performanceIndexes, setPerformanceIndexes] = useState<RunnerIndexView[]>(performanceIndexesProp);
    const [frozenIndexes, setFrozenIndexes] = useState<FrozenIndexView[] | undefined>(frozenIndexesProp);
    const [runnerStatistic, setRunnerStatistic] = useState<RunnerStatisticView>(runnerStatisticProp);
    // const [isIndexAvailable, setIsIndexAvailable] = useState<boolean>(isLogged);
    const [isMe, setIsMe] = useState<boolean>(false);

    useEffect(() => {
        async function getResults(): Promise<void> {
            const token = await getToken();

            const runner = (await fetchTheAPI({
                tenantId,
                path: `/runners/${query.runner}`,
                params: { lang: locale as string, full: 'true' },
                bearer: token,
            })) as RunnerView;

            setResults(runner.results);
            setPerformanceIndexes(runner.performanceIndexes);
            setFrozenIndexes(runner.frozenIndexes);
            setRunnerStatistic(runner.runnerStatistic);
            // setIsIndexAvailable(runner.isLogged);
        }

        if (isAuthenticated && !isLogged) {
            getResults().then();
        }
        if (isAuthenticated && me?.profileUrl) {
            const uriMe = me.profileUrl.split('/runner/')[1];
            if (uriMe === query.runner) setIsMe(true);
        }
    }, [getToken, isAuthenticated, isLogged, locale, query.runner, tenantId, me]);

    const lastResult = results.results.find((result) => result.rank > 0);
    const seoParams = {
        fullName: fullname,
        index: performanceIndexes.find((p) => p.piCategory === PiCategoryEnum.GENERAL)?.index?.toString() ?? '-',
        lastRace: lastResult ? lastResult.race : '-',
        lastResult: lastResult ? `${t('singleRunner.statistics.distribution.rank', { count: lastResult.rank })}` : '-',
        lastResultSex: lastResult ? `${t('singleRunner.statistics.distribution.rank', { count: lastResult.rankGender })}` : '-',
        lastTime: lastResult ? lastResult.time : '-',
        teamText: `${
            team || club ? t('singleRunner.runnerSEO.descriptionTeam', { context: gender === SexEnum.MEN ? 'male' : 'female', team: team || club }) : ''
        }`,
        context: gender === SexEnum.MEN ? 'male' : 'female',
    };

    const seoImage = profilePicture && pIdToCloudinary(profilePicture, fullname);
    const seoTitle = t('singleRunner.runnerSEO.title', seoParams);
    const seoDescription = t('singleRunner.runnerSEO.description', seoParams);

    const seo = {
        custom: [
            {
                description: seoDescription,
                image: seoImage,
                title: seoTitle,
                type: `${t(getGender(gender))}`,
            },
        ],
        description: seoDescription,
        image: seoImage,
        keywords: t('singleRunner.runnerSEO.keywords', seoParams),
        title: seoTitle,
    };

    const breadcrumbs = {
        lang: locale,
        slug: '/utmb-world-series/runners/the-runners',
        breadcrumbs: [
            {
                lang: locale,
                title: 'Utmb world series',
                slug: '/',
            },
            {
                lang: locale,
                title: t('breadcrumbs.runners'),
                slug: '/utmb-index/runner-search',
            },
            {
                lang: locale,
                title: fullname,
                slug: null,
            },
        ],
    };

    // const hasDetail = club || sponsor || description || team || website || socials?.length;

    const isDevMode = query.dev !== '74660';

    return (
        <PageProviders>
            <Layout
                {...(seo as any)}
                host={host}
                contentLocale={locale}
                isMobile={isMobile}
                eventsTopBar={eventsTopBar}
                mobileBottomBar={mobileBottomBar}
                navigation={navigation}
                simpleMenu={simpleMenu}
                footer={footer}
                landing={false}
                event={event}
                partners={partners}
                languages={languages}
                breadcrumbs={breadcrumbs as any}
            >
                {isDevMode ? (
                    <div className={styles.wrapRunnerPageRemake}>
                        <RunnerHeaderRemake {...props} />
                        <IndexRow performanceIndexes={performanceIndexes} frozenIndexes={frozenIndexes} results={results.results} />
                        <IndexStatisticsPalmaresArea
                            results={results.results}
                            isMe={isMe}
                            runnerStatistic={runnerStatistic}
                            profilePicture={profilePicture ?? null}
                            fullname={fullname}
                        />
                    </div>
                ) : (
                    <div>
                        {/* <RunnerHeader {...props} />

                        {hasDetail ? <RunnerMoreDetails {...props} /> : null}

                        <PerformanceIndex {...props} /> */}

                        {/*stay <DiscoverBanner mainColor={BackgroundColorEnum.WHITE} />*/}

                        {/* <ResultsTable isIndexAvailable={isIndexAvailable} results={results.results} isMe={isMe} />

                        <RunnerStatistics runnerStatistic={runnerStatistic} picture={props.profilePicture ?? null} /> */}
                    </div>
                )}
            </Layout>
        </PageProviders>
    );
};

export default Runner;

export const getServerSideProps: GetServerSideProps<PageProps | any> = async ({ locale, defaultLocale, params, req }) => {
    try {
        // Translate
        const lang: LangEnum = (locale || defaultLocale || i18n.defaultLocale) as LangEnum;
        const translations = await serverSideTranslations(lang, ['common', 'myutmb']);

        const host = req.headers.host;

        // Runner Content
        const token = req.cookies[ACCESS_TOKEN_COOKIE_NAME];

        const runner = (await fetchTheAPI({
            req,
            path: `/runners/${params?.runner}`,
            params: { lang, full: 'true' },
            bearer: token,
        })) as RunnerView;

        // if error
        if (!runner) return { notFound: true };

        return {
            props: {
                isMobile: isMobile(req),
                host,
                ...runner,
                ...translations,
            },
        };
    } catch (error) {
        return {
            notFound: true,
        };
    }
};
